<script setup>
import {computed, ref, watch} from "vue"
import FormCreateInfo from "../form/form-create-info.vue";
import PackageTrackDetails from "./package-track-details.vue";
import PackageTrackingNumber from "./package-tracking-number.vue";
import helpers from "@/lib/helpers";
import PackageCartonLabel from "./package-carton-label.vue";
import PackageStatus from "./package-status.vue";
import {useForm} from "../form/useForm";
import PackageCarrierLabel from "./package-carrier-label.vue";
import PackageDuplicate from "@/components/package/package-duplicate.vue";
import NotificationWatch from "@/components/notification/notification-watch.vue";

const emits = defineEmits(['submit', 'emit'])

const props = defineProps({
  value: {
    required: true,
    type: Object
  },
  noTransition: Boolean,
  perm: String
})

const record = ref()
watch(() => props.value, () => {
  record.value = props.value
}, {immediate: true})

const {form, changed, save, state, modify} = useForm(
    [
      'notes', 'ship_tracking', 'alt_tracking'
    ],
    null,
    record,
    null,
    (v) => emits('input', v),
)

// a freight shipment booked or entered by hand: PRO and BOL numbers, and the booking
const isFreight = computed(() => !!(props.value.shipping && props.value.shipping.freight))
const booking = computed(() => (props.value.freight && props.value.freight.booking) || null)

// the BOL and pallet labels, as one PDF: straight to the print dialog, or opened to look at
const bolReady = computed(() => isFreight.value && booking.value && props.value.has_label && !props.value.voided)
const print_bol = () => {
  const frame = document.createElement('iframe')
  frame.style.display = 'none'
  frame.src = props.value._links.carrier_label_preview_url.href
  frame.onload = () => {
    try {
      frame.contentWindow.focus()
      frame.contentWindow.print()
    } catch {
      window.open(frame.src, '_blank')
    }
    setTimeout(() => frame.remove(), 60000)
  }
  document.body.appendChild(frame)
}
const open_bol = () => window.open(props.value._links.carrier_label_preview_url.href, '_blank')

const copy_ext_url = async () => {
  if (!props.value.return_shipment)
    if (!await helpers.confirm('Label is not a return shipment, are you sure?'))
      return

  navigator.clipboard.writeText(props.value.ship_external_url)
  helpers.toast('External URL copied')
}

const track_refresh = async () => {
  const result = await helpers.asyncAction(props.value._links.self_url.href, 'PUT', {data: {task: 'track'}})
  emits('input', result)
  helpers.toast("Tracking refreshed")
}

const void_package = async () => {
  if (!await helpers.confirm('Void this shipment?'))
    return
  const result = await helpers.asyncAction(props.value._links.self_url.href, 'PUT', {data: {task: 'void'}})
  emits('input', result)
  helpers.toast("Shipment voided")
}

// a freight booking: cancelled with the carrier, and its pickup with it when the carrier agrees
const cancel_booking = async () => {
  if (!await helpers.confirm(`Cancel the ${props.value.carrier_name || 'freight'} booking and its pickup? Freight already picked up can not be cancelled.`))
    return
  const result = await helpers.asyncAction(props.value._links.self_url.href, 'PUT', {data: {task: 'void'}})
  emits('input', result)
  const b = (result.freight && result.freight.booking) || {}
  if (b.pickup_cancelled)
    helpers.toast('Booking and pickup cancelled')
  else
    helpers.toast('Booking cancelled - the carrier did not confirm the pickup was cancelled. Call them if a driver may still come.', true, 'Freight')
}

</script>

<template>
  <div>
    <modal-portal to="footer">
      <div class="package-footer">
        <b-button @click="emits('quote')" size="sm" variant="outline-secondary">Quote</b-button>

        <package-duplicate :perm="perm" v-if="!!modify" @copy="(v) => emits('copy', v)" />

        <b-button @click="save" size="sm" variant="outline-primary">Save</b-button>
      </div>
    </modal-portal>

    <package-status :value="value">
      <b-dropdown variant="link" no-caret size="sm">
        <template #button-content>
          <font-awesome-icon size="lg" class="text-secondary" icon="fa-solid fa-ellipsis-v"/>
        </template>
        <template v-if="value.download_key">
          <b-dropdown-item-button :disabled="value.voided" @click="copy_ext_url">Copy External URL
          </b-dropdown-item-button>
          <b-dropdown-divider/>
        </template>
        <b-dropdown-item :disabled="value.voided" target="_blank" :href="value.ship_track_url"
                         v-if="value.ship_track_url">Carrier Tracking
          Site
        </b-dropdown-item>
        <b-dropdown-item-button :disabled="value.voided" @click="track_refresh">Refresh Tracking
        </b-dropdown-item-button>
        <b-dropdown-item-button v-if="!value.voided && can(perm + '.packages.void') && booking" @click="cancel_booking">
          Cancel Booking &amp; Pickup
        </b-dropdown-item-button>
        <b-dropdown-item-button v-else-if="!value.voided && can(perm + '.packages.void')" @click="void_package">Void
        </b-dropdown-item-button>

        <template v-if="can('notifications.rules.read')">
          <b-dropdown-divider/>
          <b-dropdown-item-button
              @click="$dialog.show(NotificationWatch, {type: 'Package', id: value.id, gli: value.gli})">
            Notifications
          </b-dropdown-item-button>
        </template>

        <template v-if="can(perm + '.packages.api-data')">
          <b-dropdown-divider/>
          <b-dropdown-item-button
              @click="emits('submit'); $router.push({name: 'data', params: {url: value._links.request_data_url}})">API
            Request Data
          </b-dropdown-item-button>
          <b-dropdown-item-button
              @click="emits('submit'); $router.push({name: 'data', params: {url: value._links.response_data_url}})">API
            Response Data
          </b-dropdown-item-button>
          <b-dropdown-item-button
              @click="emits('submit'); $router.push({name: 'data', params: {url: value._links.track_data_url}})">API
            Tracking Data
          </b-dropdown-item-button>
        </template>
      </b-dropdown>
    </package-status>

    <b-alert show variant="success" class="d-flex align-items-center" v-if="bolReady">
      <div class="flex-fill mr-3" style="min-width: 0">
        <b>Bill of lading ready</b>
        <div class="small">BOL {{ value.alt_tracking }} and the pallet labels for {{ value.carrier_name }} - one on each pallet, the BOL to the driver.</div>
      </div>
      <b-button-group size="sm" class="flex-shrink-0">
        <b-btn variant="success" class="text-nowrap" @click="print_bol">
          <font-awesome-icon :icon="['fas', 'print']" class="mr-1"/>Print
        </b-btn>
        <b-btn variant="outline-success" title="Open the PDF" @click="open_bol">
          <font-awesome-icon :icon="['far', 'file-pdf']"/>
        </b-btn>
      </b-button-group>
    </b-alert>

    <form-card title="General">
      <modal-row label="Shipment ID" is-text>
        {{ value.gli }}
      </modal-row>
      <modal-row label="Carrier" is-text v-if="isFreight && value.carrier_name && value.shipping.websvc_label">
        {{ value.carrier_name }} <span class="text-muted">({{ value.shipping.websvc_label }}, freight)</span>
      </modal-row>
      <modal-row label="Carrier" is-text v-else>
        {{ value.shipping && value.shipping.carrier && value.shipping.carrier.description || '?' }} -
        {{ value.shipping && value.shipping.description || '?' }}
      </modal-row>
      <modal-row label="Address" is-text>
        <div class="text-pre-line">{{ value && value.ship_address_string }}</div>
      </modal-row>
      <modal-row label="Phone" is-text>{{ value.phone }}</modal-row>
      <modal-row label="Email" is-text>{{ value.email }}</modal-row>
      <form-create-info no-divider :value="value"/>
    </form-card>

    <form-card title="Options">
      <modal-row label="Declared Value" is-text>
        {{ value.ship_declared_value | money }}
      </modal-row>

      <modal-row label="Ref 1 / Ref 2" is-text>
        {{ value.reference }}
        <template v-if="value.reference2"> / {{ value.reference2 }}</template>
      </modal-row>

      <modal-row label="Signature" is-text>
        {{ value.signature ? 'YES' : 'NO' }}
      </modal-row>
      <modal-row label="Residential" is-text>
        {{ value.ship_residential ? 'YES' : 'NO' }}
      </modal-row>

      <modal-row label="Bill Account" is-text>
        <div>{{ value.ship_accountno }}</div>
        <div v-if="value.bill_zip">{{ value.bill_zip }}
          <template v-if="value.bill_country"> - {{ value.bill_country }}</template>
        </div>
      </modal-row>
    </form-card>

    <form-card title="Master Package">
      <modal-row label="Weight / Dims" is-text>
        <div v-if="!value?.shipping?.nodims">{{ value.pkg_length }}" x {{ value.pkg_width }}" x {{ value.pkg_height }}"</div>
        <div>{{ value.pkg_weight }} Lbs</div>
      </modal-row>
      <modal-row label="Ship Cost" is-text>
        {{ value.pkg_actual_ship_cost | money }}
      </modal-row>
      <modal-row label="Ship Date" is-text>
        {{ value.ship_date }}
      </modal-row>

      <modal-row label="Shipping Label" v-if="!value.voided">
        <package-carrier-label single :value="value" />
      </modal-row>

      <modal-row label="Carton Label">
        <package-carton-label :url="value._links.label_url"/>
      </modal-row>

      <form-divider/>

      <template v-if="isFreight">
        <modal-row label="PRO #" :feedback="state.ship_tracking">
          <b-input size="sm" v-model="form.ship_tracking" :state="state.ship_tracking | state"
                   placeholder="from the carrier after pickup"/>
        </modal-row>
        <modal-row label="BOL #" :feedback="state.alt_tracking">
          <b-input size="sm" v-model="form.alt_tracking" :state="state.alt_tracking | state"/>
        </modal-row>
        <modal-row label="Pickup Conf #" is-text v-if="booking && booking.confirmation_number">
          {{ booking.confirmation_number }}
        </modal-row>
        <modal-row label="Booking" is-text v-if="booking">
          {{ booking.freight_shipment_id }}
          <span class="text-muted" v-if="booking.status"> · {{ booking.status }}</span>
          <span class="text-warning" v-if="booking.cancelled"> · cancelled{{ booking.pickup_cancelled ? ', pickup cancelled' : '' }}</span>
        </modal-row>
        <modal-row label="Tracking" is-text v-if="value.ship_track_url">
          <a :href="value.ship_track_url" target="_blank" rel="noopener">{{ value.carrier_name }} tracking</a>
        </modal-row>
      </template>
      <package-tracking-number :value="value" v-else/>

      <package-track-details v-if="value.track_last_update_date" :value="value"/>
    </form-card>

    <form-card title="Additional Packages" v-if="value.group_packages && value.group_packages.length">
      <div class="accordion" role="tablist">
        <b-card no-body class="mb-1" v-for="pkg in value.group_packages" :key="pkg.id">
          <b-card-header header-tag="header" class="p-1" role="tab">
            <b-button variant="outline-secondary" v-b-toggle="'accordion-' + pkg.id" block class="d-flex" size="sm">
              <div class="flex-fill text-left">{{ pkg.pkg_length }}" x {{ pkg.pkg_width }}" x {{
                  pkg.pkg_height
                }}"
              </div>
              <div>{{ pkg.track_status_desc }}</div>
            </b-button>
          </b-card-header>

          <b-collapse :id="'accordion-' + pkg.id" accordion="pkg-accordian" role="tabpanel">
            <b-card-body>
              <modal-row label="Package ID" is-text>
                {{ pkg.gli }}
              </modal-row>
              <modal-row label="Ship Cost" is-text>
                {{ pkg.pkg_actual_ship_cost | money }}
              </modal-row>
              <modal-row label="Ship Date" is-text>
                {{ pkg.ship_date }}
              </modal-row>
              <modal-row label="Label" v-if="!pkg.voided">
                <package-carrier-label single :value="pkg" />
              </modal-row>
              <modal-row label="Carton Label">
                <package-carton-label :url="pkg._links.label_url" />
              </modal-row>
              <package-tracking-number :value="value"/>
              <package-track-details :value="pkg"/>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </form-card>

    <form-card title="Notes">
      <b-textarea v-model="form.notes" rows="5" />
    </form-card>
  </div>
</template>

<style scoped>
/* Footer buttons stay in one right-aligned row, wrapping as a group (not one stray button) on a phone */
.package-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: .5rem;
}

.package-footer > .btn {
  padding-left: 1rem;
  padding-right: 1rem;
}

@media (max-width: 575.98px) {
  .package-footer > .btn {
    padding-left: .5rem;
    padding-right: .5rem;
  }
}

</style>